import { computed, inject, Injectable, signal } from '@angular/core';

import { Store } from '@ngrx/store';
import { filter, take } from 'rxjs/operators';

import {
  ActivitiesDataScope,
  DefaultActivitiesAndNotificationsFilters,
  getStoreScope,
  TrackingActivitiesActions,
  TrackingActivitiesSelectors,
} from '@core/activities-and-notifications-data';

import { IntelligenceSettingsDataService } from './intelligence-settings-data.service';

export type IntelligenceActivitiesContext =
  | { type: 'candidate'; candidateId: number }
  | { type: 'offer'; offerId: number };

@Injectable({ providedIn: 'root' })
export class IntelligenceActivitiesTabService {
  private readonly store = inject(Store);
  private readonly intelligenceSettingsDataService = inject(IntelligenceSettingsDataService);
  private readonly hasIntelligenceActivitiesMap = new Map<string, boolean>();

  public readonly isScreeningAssistantEnabled = signal(false);
  public readonly isMatchingAssistantEnabled = signal(false);
  public readonly hasIntelligenceActivities = signal(false);

  public readonly isAiTabVisible = computed(
    () =>
      this.isScreeningAssistantEnabled() ||
      this.isMatchingAssistantEnabled() ||
      this.hasIntelligenceActivities(),
  );

  public checkScreeningAssistantEnabled(): void {
    this.intelligenceSettingsDataService.fetchIfNotLoaded();
    this.intelligenceSettingsDataService.settingsLoaded$
      .pipe(
        filter(loaded => loaded),
        take(1),
      )
      .subscribe(() => {
        const settings = this.intelligenceSettingsDataService.settings();
        const screeningFeature = settings?.find(s => s.feature === 'ai_candidate_screening');
        const matchingFeature = settings?.find(s => s.feature === 'ai_candidate_matching');
        this.isScreeningAssistantEnabled.set(screeningFeature?.enabled ?? false);
        this.isMatchingAssistantEnabled.set(matchingFeature?.enabled ?? false);
      });
  }

  public checkIntelligenceActivities(context: IntelligenceActivitiesContext): void {
    const entityId = context.type === 'candidate' ? context.candidateId : context.offerId;
    const cacheKey = `${context.type}-${entityId}`;

    if (this.hasIntelligenceActivitiesMap.has(cacheKey)) {
      this.hasIntelligenceActivities.set(this.hasIntelligenceActivitiesMap.get(cacheKey)!);
      return;
    }

    const scopeType =
      context.type === 'candidate' ? ActivitiesDataScope.Candidate : ActivitiesDataScope.Offer;
    const scope = getStoreScope(scopeType, entityId) + '-intelligence-check';

    const filters = {
      ...DefaultActivitiesAndNotificationsFilters,
      limit: 1,
      intelligence: true,
    };

    if (context.type === 'candidate') {
      this.store.dispatch(
        TrackingActivitiesActions.fetchCandidateTrackingActivities({
          payload: {
            params: { candidateId: context.candidateId, filters },
            scope,
            replace: true,
          },
        }),
      );
    } else {
      this.store.dispatch(
        TrackingActivitiesActions.fetchOfferTrackingActivities({
          payload: {
            params: { offerId: context.offerId, filters },
            scope,
            replace: true,
          },
        }),
      );
    }

    this.store
      .select(TrackingActivitiesSelectors.isLoaded({ scope }))
      .pipe(
        filter(loaded => loaded),
        take(1),
      )
      .subscribe(() => {
        const activities = this.store.selectSignal(
          TrackingActivitiesSelectors.entities({ scope }),
        )();
        const hasActivities = (activities?.length ?? 0) > 0;
        this.hasIntelligenceActivitiesMap.set(cacheKey, hasActivities);
        this.hasIntelligenceActivities.set(hasActivities);
      });
  }

  public reset(): void {
    this.hasIntelligenceActivities.set(false);
  }
}
